import type { Anchor, AnchorSide, Relation, Table } from '@/types'
import {
  clamp01,
  getColumnCenterY,
  getTableBounds,
  type Point,
  type TableBounds,
} from './geometry'

const RELATION_STUB = 24
const MIN_ANCHOR_MARGIN = 16
const FLIP_HYSTERESIS = 20

export interface ConnectionAnchorPoint extends Point {
  side: AnchorSide
  offsetPercent: number
}

export interface PathSegment {
  start: Point
  end: Point
  axis: 'horizontal' | 'vertical'
  length: number
}

export interface PathResult {
  d: string
  points: Point[]
  midPoint: Point
  fromPoint: ConnectionAnchorPoint
  toPoint: ConnectionAnchorPoint
  fromSide: AnchorSide
  toSide: AnchorSide
}

export function getConnectionDirection(side: AnchorSide): Point {
  switch (side) {
    case 'left':
      return { x: -1, y: 0 }
    case 'right':
      return { x: 1, y: 0 }
    case 'top':
      return { x: 0, y: -1 }
    case 'bottom':
      return { x: 0, y: 1 }
  }
}

// Clamp ratio so the anchor never lands in the corner margins
function clampAnchorRatioToMargin(ratio: number, dimensionSize: number): number {
  if (dimensionSize <= MIN_ANCHOR_MARGIN * 2) return 0.5
  const minRatio = MIN_ANCHOR_MARGIN / dimensionSize
  const maxRatio = 1 - MIN_ANCHOR_MARGIN / dimensionSize
  return Math.max(minRatio, Math.min(maxRatio, clamp01(ratio)))
}

export function getConnectionAnchor(
  tableRect: TableBounds,
  connectionPoint: Partial<Anchor> | null | undefined,
): ConnectionAnchorPoint {
  const side = connectionPoint?.side ?? 'right'
  const rawRatio = connectionPoint?.offsetPercent ?? 0.5

  let offsetPercent: number
  switch (side) {
    case 'left':
    case 'right':
      offsetPercent = clampAnchorRatioToMargin(rawRatio, tableRect.h)
      break
    case 'top':
    case 'bottom':
      offsetPercent = clampAnchorRatioToMargin(rawRatio, tableRect.w)
      break
  }

  switch (side) {
    case 'left':
      return {
        x: tableRect.x,
        y: tableRect.y + tableRect.h * offsetPercent,
        side,
        offsetPercent,
      }
    case 'right':
      return {
        x: tableRect.x + tableRect.w,
        y: tableRect.y + tableRect.h * offsetPercent,
        side,
        offsetPercent,
      }
    case 'top':
      return {
        x: tableRect.x + tableRect.w * offsetPercent,
        y: tableRect.y,
        side,
        offsetPercent,
      }
    case 'bottom':
      return {
        x: tableRect.x + tableRect.w * offsetPercent,
        y: tableRect.y + tableRect.h,
        side,
        offsetPercent,
      }
  }
}

export function snapToTableBorder(
  x: number,
  y: number,
  table: Pick<Table, 'x' | 'y' | 'cols'>,
): Anchor {
  const bounds = getTableBounds(table)
  const dLeft = Math.abs(x - bounds.x)
  const dRight = Math.abs(x - (bounds.x + bounds.w))
  const dTop = Math.abs(y - bounds.y)
  const dBottom = Math.abs(y - (bounds.y + bounds.h))
  const minDistance = Math.min(dLeft, dRight, dTop, dBottom)

  if (minDistance === dLeft) {
    return {
      side: 'left',
      offsetPercent: clampAnchorRatioToMargin(clamp01((y - bounds.y) / bounds.h), bounds.h),
    }
  }
  if (minDistance === dRight) {
    return {
      side: 'right',
      offsetPercent: clampAnchorRatioToMargin(clamp01((y - bounds.y) / bounds.h), bounds.h),
    }
  }
  if (minDistance === dTop) {
    return {
      side: 'top',
      offsetPercent: clampAnchorRatioToMargin(clamp01((x - bounds.x) / bounds.w), bounds.w),
    }
  }
  return {
    side: 'bottom',
    offsetPercent: clampAnchorRatioToMargin(clamp01((x - bounds.x) / bounds.w), bounds.w),
  }
}

export function resolveAnchor(
  anchor: Anchor | null | undefined,
  table: Pick<Table, 'x' | 'y' | 'cols'>,
): ConnectionAnchorPoint | null {
  if (!anchor) return null
  return getConnectionAnchor(getTableBounds(table), anchor)
}

// Returns true if the anchor side no longer makes sense given the relative position of the other table.
// Includes hysteresis (FLIP_HYSTERESIS px) to avoid flip-flop during small movements,
// plus a forced flip when the stub segment would cross the source table's own interior.
export function shouldFlipAnchorSide(
  anchor: Anchor,
  fromRect: TableBounds,
  toRect: TableBounds,
): boolean {
  const toCX = toRect.x + toRect.w / 2
  const toCY = toRect.y + toRect.h / 2

  let positionInverted: boolean
  switch (anchor.side) {
    case 'right':
      positionInverted = toCX < fromRect.x - FLIP_HYSTERESIS
      break
    case 'left':
      positionInverted = toCX > fromRect.x + fromRect.w + FLIP_HYSTERESIS
      break
    case 'bottom':
      positionInverted = toCY < fromRect.y - FLIP_HYSTERESIS
      break
    case 'top':
      positionInverted = toCY > fromRect.y + fromRect.h + FLIP_HYSTERESIS
      break
  }
  if (positionInverted) return true

  // Force flip if the stub would go through the source table itself
  const anchorPt = getConnectionAnchor(fromRect, anchor)
  const dir = getConnectionDirection(anchor.side)
  const stubEnd = offsetPoint(anchorPt, dir, RELATION_STUB)
  if (doesSegmentIntersectRect(anchorPt, stubEnd, toRect)) return true

  return false
}

export function flipAnchor(anchor: Anchor): Anchor {
  const opposites: Record<AnchorSide, AnchorSide> = {
    left: 'right',
    right: 'left',
    top: 'bottom',
    bottom: 'top',
  }
  return { side: opposites[anchor.side], offsetPercent: anchor.offsetPercent }
}

// Returns true if a segment between p1 and p2 overlaps with the given rect
export function doesSegmentIntersectRect(p1: Point, p2: Point, rect: TableBounds): boolean {
  const minX = Math.min(p1.x, p2.x)
  const maxX = Math.max(p1.x, p2.x)
  const minY = Math.min(p1.y, p2.y)
  const maxY = Math.max(p1.y, p2.y)
  return (
    minX < rect.x + rect.w &&
    maxX > rect.x &&
    minY < rect.y + rect.h &&
    maxY > rect.y
  )
}

function offsetPoint(point: Point, vector: Point, distance: number): Point {
  return {
    x: point.x + vector.x * distance,
    y: point.y + vector.y * distance,
  }
}

const MIN_SEGMENT_LENGTH = 4

function isOrthogonalSegment(a: Point, b: Point): boolean {
  return Math.round(a.x) === Math.round(b.x) || Math.round(a.y) === Math.round(b.y)
}

function insertOrthogonalBend(a: Point, b: Point): Point {
  return { x: b.x, y: a.y }
}

function simplifyOrthogonalPoints(points: Point[]): Point[] {
  const rounded = points.map((point) => ({
    x: Math.round(point.x * 100) / 100,
    y: Math.round(point.y * 100) / 100,
  }))

  // Merge points that are too close together (< MIN_SEGMENT_LENGTH apart).
  // When skipping a point, align the previous point to the next one on the
  // dominant axis so no diagonal is introduced.
  const merged: Point[] = [rounded[0]]
  for (let i = 1; i < rounded.length; i++) {
    const prev = merged[merged.length - 1]
    const curr = rounded[i]
    const dist = Math.abs(curr.x - prev.x) + Math.abs(curr.y - prev.y)
    if (dist < MIN_SEGMENT_LENGTH && i < rounded.length - 1) {
      const next = rounded[i + 1]
      // Only merge tiny bends when removing them preserves an orthogonal hop.
      if (isOrthogonalSegment(prev, next)) {
        continue
      }
    }
    merged.push(curr)
  }

  const deduped = merged

  // Remove collinear midpoints
  let changed = true
  while (changed) {
    changed = false
    for (let i = 1; i < deduped.length - 1; i++) {
      const a = deduped[i - 1]
      const b = deduped[i]
      const c = deduped[i + 1]
      if ((a.x === b.x && b.x === c.x) || (a.y === b.y && b.y === c.y)) {
        deduped.splice(i, 1)
        changed = true
        break
      }
    }
  }

  return deduped
}

export function getOrthogonalPathBetweenAnchors(
  sourceAnchor: ConnectionAnchorPoint,
  targetAnchor: ConnectionAnchorPoint,
  midOffset = 0,
): Point[] {
  const sourceDirection = getConnectionDirection(sourceAnchor.side)
  const targetDirection = getConnectionDirection(targetAnchor.side)
  const sourceStub = offsetPoint(sourceAnchor, sourceDirection, RELATION_STUB)
  const targetStub = offsetPoint(targetAnchor, targetDirection, RELATION_STUB)
  const points: Point[] = [sourceAnchor, sourceStub]

  if (sourceStub.x === targetStub.x || sourceStub.y === targetStub.y) {
    points.push(targetStub)
  } else if (
    (sourceAnchor.side === 'top' || sourceAnchor.side === 'bottom') &&
    (targetAnchor.side === 'top' || targetAnchor.side === 'bottom')
  ) {
    const midY = (sourceStub.y + targetStub.y) / 2 + midOffset
    points.push({ x: sourceStub.x, y: midY }, { x: targetStub.x, y: midY })
  } else if (
    (sourceAnchor.side === 'left' || sourceAnchor.side === 'right') &&
    (targetAnchor.side === 'left' || targetAnchor.side === 'right')
  ) {
    const midX = (sourceStub.x + targetStub.x) / 2 + midOffset
    points.push({ x: midX, y: sourceStub.y }, { x: midX, y: targetStub.y })
  } else if (sourceAnchor.side === 'top' || sourceAnchor.side === 'bottom') {
    points.push({ x: sourceStub.x, y: targetStub.y })
  } else if (targetAnchor.side === 'top' || targetAnchor.side === 'bottom') {
    points.push({ x: targetStub.x, y: sourceStub.y })
  } else {
    const midX = (sourceStub.x + targetStub.x) / 2 + midOffset
    points.push({ x: midX, y: sourceStub.y }, { x: midX, y: targetStub.y })
  }

  points.push(targetStub, targetAnchor)
  return simplifyOrthogonalPoints(points)
}

function buildOrthogonalPathFromPoints(points: Point[]): string {
  if (!points.length) return ''
  if (points.length === 1) return `M${points[0].x},${points[0].y}`

  const commands = [`M${points[0].x},${points[0].y}`]

  for (let i = 1; i < points.length; i++) {
    const current = points[i]
    commands.push(`L${current.x},${current.y}`)
  }

  return commands.join(' ')
}

export function getPathMidPoint(points: Point[]): Point {
  if (!points.length) return { x: 0, y: 0 }
  if (points.length === 1) return points[0]

  const half = getOrthogonalPathLength(points) / 2
  let walked = 0

  for (let i = 1; i < points.length; i++) {
    const start = points[i - 1]
    const end = points[i]
    const segmentLength =
      Math.abs(end.x - start.x) + Math.abs(end.y - start.y)

    if (walked + segmentLength >= half) {
      const remaining = half - walked
      if (start.x === end.x) {
        return {
          x: start.x,
          y: start.y + Math.sign(end.y - start.y) * remaining,
        }
      }
      return {
        x: start.x + Math.sign(end.x - start.x) * remaining,
        y: start.y,
      }
    }

    walked += segmentLength
  }

  return points[points.length - 1]
}

export function getPointAtT(points: Point[], t: number): Point {
  if (!points.length) return { x: 0, y: 0 }
  if (points.length === 1) return points[0]
  const total = getOrthogonalPathLength(points)
  if (total === 0) return points[0]
  const target = Math.max(0, Math.min(1, t)) * total
  let walked = 0
  for (let i = 1; i < points.length; i++) {
    const start = points[i - 1]
    const end = points[i]
    const segLen = Math.abs(end.x - start.x) + Math.abs(end.y - start.y)
    if (walked + segLen >= target) {
      const rem = target - walked
      if (start.x === end.x) return { x: start.x, y: start.y + Math.sign(end.y - start.y) * rem }
      return { x: start.x + Math.sign(end.x - start.x) * rem, y: start.y }
    }
    walked += segLen
  }
  return points[points.length - 1]
}

export function getPathAxisAtT(points: Point[], t: number): 'horizontal' | 'vertical' {
  if (points.length < 2) return 'horizontal'
  const total = getOrthogonalPathLength(points)
  const target = Math.max(0, Math.min(1, t)) * total
  let walked = 0
  for (let i = 1; i < points.length; i++) {
    const start = points[i - 1]
    const end = points[i]
    const segLen = Math.abs(end.x - start.x) + Math.abs(end.y - start.y)
    if (walked + segLen >= target) {
      return start.y === end.y ? 'horizontal' : 'vertical'
    }
    walked += segLen
  }
  const last = points[points.length - 2]
  const fin  = points[points.length - 1]
  return last.y === fin.y ? 'horizontal' : 'vertical'
}

export function getPathTangentAtT(points: Point[], t: number): { dx: number; dy: number } {
  if (points.length < 2) return { dx: 1, dy: 0 }
  const total = getOrthogonalPathLength(points)
  if (total === 0) return { dx: 1, dy: 0 }
  const target = Math.max(0, Math.min(1, t)) * total
  let walked = 0
  for (let i = 1; i < points.length; i++) {
    const start = points[i - 1]
    const end = points[i]
    const segLen = Math.abs(end.x - start.x) + Math.abs(end.y - start.y)
    if (walked + segLen >= target) {
      return { dx: segLen ? (end.x - start.x) / segLen : 0, dy: segLen ? (end.y - start.y) / segLen : 0 }
    }
    walked += segLen
  }
  const last = points[points.length - 2]
  const fin  = points[points.length - 1]
  const segLen = Math.abs(fin.x - last.x) + Math.abs(fin.y - last.y)
  return { dx: segLen ? (fin.x - last.x) / segLen : 1, dy: segLen ? (fin.y - last.y) / segLen : 0 }
}

export function getOrthogonalPathLength(points: Point[]): number {
  let total = 0
  for (let i = 1; i < points.length; i++) {
    total +=
      Math.abs(points[i].x - points[i - 1].x) +
      Math.abs(points[i].y - points[i - 1].y)
  }
  return total
}

export function getCardinalityRotation(
  lastSegmentVector: Point,
  connectionSide: AnchorSide,
): AnchorSide {
  if (Math.abs(lastSegmentVector.x) > Math.abs(lastSegmentVector.y)) {
    return lastSegmentVector.x >= 0 ? 'right' : 'left'
  }
  if (Math.abs(lastSegmentVector.y) > 0) {
    return lastSegmentVector.y >= 0 ? 'bottom' : 'top'
  }
  return connectionSide
}

export function splitPathSegments(points: Point[]): PathSegment[] {
  const segments: PathSegment[] = []

  for (let i = 1; i < points.length; i++) {
    const start = points[i - 1]
    const end = points[i]
    const horizontal = Math.abs(end.x - start.x) >= Math.abs(end.y - start.y)

    segments.push({
      start,
      end,
      axis: horizontal ? 'horizontal' : 'vertical',
      length:
        Math.abs(end.x - start.x) + Math.abs(end.y - start.y),
    })
  }

  return segments
}

// Fix any diagonal segments by inserting a bend point (H then V),
// then collapse redundant collinear points to keep the path minimal.
export function ensureOrthogonalSegments(pts: { x: number; y: number }[]): { x: number; y: number }[] {
  if (pts.length < 2) return pts
  const result: { x: number; y: number }[] = [pts[0]]
  for (let i = 1; i < pts.length; i++) {
    const prev = result[result.length - 1]
    const curr = pts[i]
    if (!isOrthogonalSegment(prev, curr)) {
      result.push(insertOrthogonalBend(prev, curr))
    }
    result.push(curr)
  }

  const simplified = simplifyOrthogonalPoints(result)
  if (simplified.length < 2) return simplified

  const normalized: Point[] = [simplified[0]]
  for (let i = 1; i < simplified.length; i++) {
    const prev = normalized[normalized.length - 1]
    const curr = simplified[i]
    if (!isOrthogonalSegment(prev, curr)) {
      normalized.push(insertOrthogonalBend(prev, curr))
    }
    normalized.push(curr)
  }

  return normalized
}

// Returns true if the segment a→b is horizontal (wider than tall)
function isSegmentHorizontal(a: Point, b: Point): boolean {
  return Math.abs(b.y - a.y) <= Math.abs(b.x - a.x)
}

// Propagate orthogonality from movedIdx outward in both directions.
// Uses original segment orientations so angles always stay at 90°.
// Modifies `pts` in-place.
export function enforceOrthogonalPropagation(
  pts: { x: number; y: number }[],
  original: { x: number; y: number }[],
  movedIdx: number,
): void {
  // Forward — only the immediate neighbor
  if (movedIdx < pts.length - 1) {
    if (isSegmentHorizontal(original[movedIdx], original[movedIdx + 1])) {
      pts[movedIdx + 1].y = pts[movedIdx].y
    } else {
      pts[movedIdx + 1].x = pts[movedIdx].x
    }
  }
  // Backward — only the immediate neighbor
  if (movedIdx > 0) {
    if (isSegmentHorizontal(original[movedIdx - 1], original[movedIdx])) {
      pts[movedIdx - 1].y = pts[movedIdx].y
    } else {
      pts[movedIdx - 1].x = pts[movedIdx].x
    }
  }
}

// Clamp an anchor endpoint to its table edge (anchor can slide along the edge but cannot leave it).
// After clamping, re-aligns the adjacent waypoint so the first/last segment stays 90°.
export function clampAnchorToEdge(
  pts: { x: number; y: number }[],
  anchorIdx: number,
  side: AnchorSide,
  rect: TableBounds,
  original: { x: number; y: number }[],
): void {
  const p = pts[anchorIdx]

  // Fix the axis perpendicular to the side (anchor is pinned to the edge)
  if (side === 'left' || side === 'right') {
    p.x = side === 'left' ? rect.x : rect.x + rect.w
    p.y = Math.max(rect.y + MIN_ANCHOR_MARGIN, Math.min(rect.y + rect.h - MIN_ANCHOR_MARGIN, p.y))
  } else {
    p.y = side === 'top' ? rect.y : rect.y + rect.h
    p.x = Math.max(rect.x + MIN_ANCHOR_MARGIN, Math.min(rect.x + rect.w - MIN_ANCHOR_MARGIN, p.x))
  }

  // Re-align adjacent waypoint so the stub segment remains 90°
  const isLast = anchorIdx === pts.length - 1
  const adjIdx = isLast ? anchorIdx - 1 : anchorIdx + 1
  if (adjIdx >= 0 && adjIdx < pts.length) {
    const oA = isLast ? original[anchorIdx - 1] : original[anchorIdx]
    const oB = isLast ? original[anchorIdx] : original[anchorIdx + 1]
    if (isSegmentHorizontal(oA, oB)) {
      pts[adjIdx].y = p.y
    } else {
      pts[adjIdx].x = p.x
    }
  }
}

// Infers the best anchor side from where the adjacent waypoint sits relative to the table.
// Prefers the edge from which the waypoint is furthest outside; falls back to center direction.
function chooseSideFromAdjacentPoint(adj: Point, rect: TableBounds): AnchorSide {
  const dLeft   = rect.x - adj.x
  const dRight  = adj.x - (rect.x + rect.w)
  const dTop    = rect.y - adj.y
  const dBottom = adj.y - (rect.y + rect.h)
  const maxDist = Math.max(dLeft, dRight, dTop, dBottom)

  if (maxDist >= RELATION_STUB / 2) {
    if (dLeft   === maxDist) return 'left'
    if (dRight  === maxDist) return 'right'
    if (dTop    === maxDist) return 'top'
    return 'bottom'
  }

  // adj is inside or very close to the table — use direction from table center
  const dx = adj.x - (rect.x + rect.w / 2)
  const dy = adj.y - (rect.y + rect.h / 2)
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? 'right' : 'left'
  return dy >= 0 ? 'bottom' : 'top'
}

// Clamp anchor to its table edge with automatic side switching.
// Determines the best side from the adjacent waypoint direction, moves the anchor to that edge,
// and realigns the adjacent waypoint so the stub stays at 90°.
// Returns the side that was actually applied (may differ from the original).
export function clampAnchorWithSideSwitch(
  pts: { x: number; y: number }[],
  anchorIdx: number,
  rect: TableBounds,
): AnchorSide {
  const isLast = anchorIdx === pts.length - 1
  const adjIdx = isLast ? anchorIdx - 1 : anchorIdx + 1
  if (adjIdx < 0 || adjIdx >= pts.length) return 'right'

  const p    = pts[anchorIdx]
  const adj  = pts[adjIdx]
  const side = chooseSideFromAdjacentPoint(adj, rect)

  // Anchor slides along the table edge to follow the adjacent bend point,
  // rather than overriding the bend's position.
  if (side === 'left' || side === 'right') {
    p.x = side === 'left' ? rect.x : rect.x + rect.w
    p.y = Math.max(rect.y + MIN_ANCHOR_MARGIN, Math.min(rect.y + rect.h - MIN_ANCHOR_MARGIN, adj.y))
    adj.y = p.y
  } else {
    p.y = side === 'top' ? rect.y : rect.y + rect.h
    p.x = Math.max(rect.x + MIN_ANCHOR_MARGIN, Math.min(rect.x + rect.w - MIN_ANCHOR_MARGIN, adj.x))
    adj.x = p.x
  }

  return side
}

// Convert a clamped anchor point back to an Anchor descriptor
export function anchorFromPoint(p: Point, side: AnchorSide, rect: TableBounds): Anchor {
  const offsetPercent = (side === 'left' || side === 'right')
    ? clampAnchorRatioToMargin((p.y - rect.y) / rect.h, rect.h)
    : clampAnchorRatioToMargin((p.x - rect.x) / rect.w, rect.w)
  return { side, offsetPercent }
}

function getAutoAnchorCandidates(
  tableRect: TableBounds,
  otherRect: TableBounds,
  preferredOffsetPercent?: number,
  parallelOffsetPx = 0,
): Anchor[] {
  const otherCenterX = otherRect.x + otherRect.w / 2
  const otherCenterY = otherRect.y + otherRect.h / 2
  const lateralOffsetAdjustment = parallelOffsetPx / Math.max(tableRect.h, 1)
  const horizontalOffsetAdjustment = parallelOffsetPx / Math.max(tableRect.w, 1)
  const lateralOffsetPercent = preferredOffsetPercent !== undefined
    ? clamp01(preferredOffsetPercent + lateralOffsetAdjustment)
    : clamp01((otherCenterY - tableRect.y) / Math.max(tableRect.h, 1) + lateralOffsetAdjustment)

  return [
    {
      side: 'left',
      offsetPercent: lateralOffsetPercent,
    },
    {
      side: 'right',
      offsetPercent: lateralOffsetPercent,
    },
    {
      side: 'top',
      offsetPercent: clamp01((otherCenterX - tableRect.x) / tableRect.w + horizontalOffsetAdjustment),
    },
    {
      side: 'bottom',
      offsetPercent: clamp01((otherCenterX - tableRect.x) / tableRect.w + horizontalOffsetAdjustment),
    },
  ]
}

function resolveParallelAnchorOffsetPx(mid: number | null | undefined): number {
  if (typeof mid !== 'number' || mid === 0) return 0
  const level = Math.max(1, Math.ceil(Math.abs(mid) / 48))
  return Math.sign(mid) * level * 8
}

function scoreRoute(
  points: Point[],
  otherTableBounds?: TableBounds[],
  sourceRect?: TableBounds,
  targetRect?: TableBounds,
): number {
  const baseScore = getOrthogonalPathLength(points) + Math.max(0, points.length - 2) * 6

  let intersections = 0

  // Penalize crossing through other tables
  if (otherTableBounds?.length) {
    for (let i = 1; i < points.length; i++) {
      for (const rect of otherTableBounds) {
        if (doesSegmentIntersectRect(points[i - 1], points[i], rect)) {
          intersections++
        }
      }
    }
  }

  // Penalize interior segments (skip stubs) crossing through source/target tables
  if (sourceRect || targetRect) {
    for (let i = 2; i < points.length - 2; i++) {
      if (sourceRect && doesSegmentIntersectRect(points[i - 1], points[i], sourceRect)) {
        intersections++
      }
      if (targetRect && doesSegmentIntersectRect(points[i - 1], points[i], targetRect)) {
        intersections++
      }
    }
  }

  return intersections * 50000 + baseScore
}

function assertOrthogonal(points: Point[]): void {
  if (import.meta.env.PROD) return
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]
    const curr = points[i]
    if (!isOrthogonalSegment(prev, curr)) {
      console.warn('Diagonal segment detected:', prev, '→', curr)
    }
  }
}

const SELF_REF_STUB = RELATION_STUB * 2

function calcSelfReferencePath(
  relation: Pick<Relation, 'col' | 'mid' | 'fromAnchor' | 'toAnchor'>,
  table: Pick<Table, 'x' | 'y' | 'cols'>,
): PathResult {
  const rect = getTableBounds(table)
  const colIndex = table.cols.findIndex((c) => c.name === relation.col)
  const sourceRatio = clampAnchorRatioToMargin(
    colIndex >= 0 ? (getColumnCenterY(table, relation.col) - table.y) / rect.h : 0.3,
    rect.h,
  )
  const targetRatio = clampAnchorRatioToMargin(
    sourceRatio < 0.5 ? Math.min(sourceRatio + 0.2, 0.9) : Math.max(sourceRatio - 0.2, 0.1),
    rect.h,
  )

  const fromAnchor: Anchor = relation.fromAnchor ?? { side: 'right', offsetPercent: sourceRatio }
  const toAnchor: Anchor = relation.toAnchor ?? { side: 'right', offsetPercent: targetRatio }
  const fromPoint = getConnectionAnchor(rect, fromAnchor)
  const toPoint = getConnectionAnchor(rect, toAnchor)

  const stubX = fromPoint.side === 'left'
    ? rect.x - SELF_REF_STUB
    : rect.x + rect.w + SELF_REF_STUB

  const points = simplifyOrthogonalPoints([
    fromPoint,
    { x: stubX, y: fromPoint.y },
    { x: stubX, y: toPoint.y },
    toPoint,
  ])

  return {
    d: buildOrthogonalPathFromPoints(points),
    points,
    midPoint: getPathMidPoint(points),
    fromPoint,
    toPoint,
    fromSide: fromPoint.side,
    toSide: toPoint.side,
  }
}

export function calcPath(
  relation: Pick<Relation, 'col' | 'mid' | 'fromAnchor' | 'toAnchor' | 'isManual' | 'waypoints'>,
  fromTable: Pick<Table, 'x' | 'y' | 'cols'>,
  toTable: Pick<Table, 'x' | 'y' | 'cols'>,
  allTables?: Pick<Table, 'x' | 'y' | 'cols'>[],
): PathResult {
  if (fromTable === toTable) {
    return calcSelfReferencePath(relation, fromTable)
  }

  const fromRect = getTableBounds(fromTable)
  const toRect = getTableBounds(toTable)

  // Auto-flip anchors if the relative position between tables has inverted
  let fromAnchor = relation.fromAnchor
  let toAnchor = relation.toAnchor

  if (fromAnchor?.side && shouldFlipAnchorSide(fromAnchor, fromRect, toRect)) {
    fromAnchor = flipAnchor(fromAnchor)
  }
  if (toAnchor?.side && shouldFlipAnchorSide(toAnchor, toRect, fromRect)) {
    toAnchor = flipAnchor(toAnchor)
  }

  // Bounds for tables OTHER than source and target (used for intersection scoring)
  const otherTableBounds = allTables
    ?.filter((t) => t !== fromTable && t !== toTable)
    .map((t) => getTableBounds(t))

  // Manual route: use stored waypoints directly
  if (relation.isManual && relation.waypoints?.length) {
    const fromPoint = getConnectionAnchor(fromRect, fromAnchor ?? { side: 'right', offsetPercent: 0.5 })
    const toPoint = getConnectionAnchor(toRect, toAnchor ?? { side: 'left', offsetPercent: 0.5 })

    // Re-align the stub waypoints so the first and last segments stay orthogonal
    // even when tables have moved since the waypoints were stored.
    // For left/right anchors the stub is horizontal → align y; for top/bottom → align x.
    const waypoints = relation.waypoints.map((p) => ({ ...p }))
    const w0 = waypoints[0]
    if (fromPoint.side === 'left' || fromPoint.side === 'right') {
      w0.y = fromPoint.y
    } else {
      w0.x = fromPoint.x
    }
    const wLast = waypoints[waypoints.length - 1]
    if (toPoint.side === 'left' || toPoint.side === 'right') {
      wLast.y = toPoint.y
    } else {
      wLast.x = toPoint.x
    }

    const raw = [fromPoint, ...waypoints, toPoint]
    const points = ensureOrthogonalSegments(raw) as Point[]
    assertOrthogonal(points)
    return {
      d: buildOrthogonalPathFromPoints(points),
      points,
      midPoint: getPathMidPoint(points),
      fromPoint,
      toPoint,
      fromSide: fromPoint.side,
      toSide: toPoint.side,
    }
  }

  // Auto-calculate: pick best anchor combination
  const preferredOffsetPercent = clamp01(
    (getColumnCenterY(fromTable, relation.col) - fromTable.y) /
      Math.max(fromRect.h, 1),
  )
  const parallelAnchorOffsetPx = resolveParallelAnchorOffsetPx(relation.mid)

  const fromCandidates = fromAnchor?.side
    ? [fromAnchor]
    : getAutoAnchorCandidates(
      fromRect,
      toRect,
      preferredOffsetPercent,
      parallelAnchorOffsetPx,
    )

  const toCandidates = toAnchor?.side
    ? [toAnchor]
    : getAutoAnchorCandidates(
      toRect,
      fromRect,
      undefined,
      parallelAnchorOffsetPx,
    )

  // Try multiple perpendicular offsets so the router can find paths that go around obstacles.
  // The stored relation.mid (used for parallel-route spreading) is always included.
  // ±300 offsets are omitted — they rarely win over ±200 and double the candidate count.
  const relMid = typeof relation.mid === 'number' ? relation.mid : 0
  const midOffsets = Array.from(new Set([0, relMid, 100, -100, 200, -200]))

  let best: {
    score: number
    fromPoint: ConnectionAnchorPoint
    toPoint: ConnectionAnchorPoint
    points: Point[]
  } | null = null

  for (const fa of fromCandidates) {
    const fromPoint = getConnectionAnchor(fromRect, fa)
    for (const ta of toCandidates) {
      const toPoint = getConnectionAnchor(toRect, ta)
      for (const midOffset of midOffsets) {
        const points = getOrthogonalPathBetweenAnchors(fromPoint, toPoint, midOffset)
        const score = scoreRoute(points, otherTableBounds, fromRect, toRect)
        if (!best || score < best.score) {
          best = { score, fromPoint, toPoint, points }
        }
      }
    }
  }

  if (!best) {
    const fallbackFrom = getConnectionAnchor(fromRect, fromAnchor)
    const fallbackTo = getConnectionAnchor(toRect, toAnchor)
    const fallbackPoints = getOrthogonalPathBetweenAnchors(fallbackFrom, fallbackTo, relMid)
    assertOrthogonal(fallbackPoints)

    return {
      d: buildOrthogonalPathFromPoints(fallbackPoints),
      points: fallbackPoints,
      midPoint: getPathMidPoint(fallbackPoints),
      fromPoint: fallbackFrom,
      toPoint: fallbackTo,
      fromSide: fallbackFrom.side,
      toSide: fallbackTo.side,
    }
  }

  assertOrthogonal(best.points)

  return {
    d: buildOrthogonalPathFromPoints(best.points),
    points: best.points,
    midPoint: getPathMidPoint(best.points),
    fromPoint: best.fromPoint,
    toPoint: best.toPoint,
    fromSide: best.fromPoint.side,
    toSide: best.toPoint.side,
  }
}

/**
 * Calculates an automatic route once and stores the result using the same
 * representation produced by the waypoint editor. This keeps the chosen
 * anchors and bends stable while tables move instead of re-running the route
 * competition on every render.
 */
export function pinRelationshipRoute(
  relation: Relation,
  tables: Table[],
): Relation {
  if (relation.isManual && relation.waypoints?.length) return relation

  const fromTable = tables.find((table) => table.id === relation.from)
  const toTable = tables.find((table) => table.id === relation.to)
  if (!fromTable || !toTable) return relation

  const path = calcPath(
    { ...relation, isManual: false, waypoints: undefined },
    fromTable,
    toTable,
    tables,
  )

  return {
    ...relation,
    fromAnchor: {
      side: path.fromPoint.side,
      offsetPercent: path.fromPoint.offsetPercent,
    },
    toAnchor: {
      side: path.toPoint.side,
      offsetPercent: path.toPoint.offsetPercent,
    },
    waypoints: path.points.slice(1, -1).map((point) => ({
      x: point.x,
      y: point.y,
    })),
    isManual: true,
  }
}

export function pinAutomaticRelationshipRoutes(
  relations: Relation[],
  tables: Table[],
): Relation[] {
  return relations.map((relation) => pinRelationshipRoute(relation, tables))
}
